import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import "./Carousel.css";

export interface CarouselSlide {
  id: string;
  content: ReactNode;
}

interface CarouselProps {
  slides: CarouselSlide[];
  /** "rtl" | "ltr" — drives the keyboard arrow-key mapping only; all *visual* mirroring (slide
   * order, control order, chevron direction) comes for free from the ambient `dir` the browser
   * already inherits from `<html dir>` (flex "row" + native `scrollIntoView` are both
   * direction-aware — see Carousel.css / the comment on `handleKeyDown` below). */
  dir: "rtl" | "ltr";
  ariaLabel: string;
  prevLabel: string;
  nextLabel: string;
  slideLabel: (index: number, total: number) => string;
  goToLabel: (index: number, total: number) => string;
  /** Autoplay interval in ms. Omit/0 disables autoplay entirely. Always disabled when the user
   * prefers reduced motion, regardless of this prop. */
  autoplayMs?: number;
  className?: string;
}

/**
 * Lightweight, dependency-free carousel built on a native horizontally-scrolling flex track
 * (`overflow-x: auto` + `scroll-snap-type: x mandatory`) rather than a JS transform engine:
 *
 * - **SSR/prerender-safe**: every slide is a plain list item in the markup returned by this
 *   component, so the *exact same* tree renders in the prerendered HTML and after hydration —
 *   there's no client-only slide list. With JS disabled entirely, the section still degrades to a
 *   perfectly usable native scroll-snap list (swipe/trackpad/keyboard-scroll all work with zero
 *   script); JS only *adds* the prev/next buttons' behaviour, dot sync, keyboard shortcuts, drag,
 *   and autoplay on top.
 * - **RTL-correct without any manual sign-flipping**: the track is a plain `display:flex` row and
 *   inherits `dir` from `<html>`, so the browser itself lays slides out start-to-end per the
 *   actual writing direction (mirrored automatically for Arabic). Navigating "next"/"prev" always
 *   just moves one step forward/back through the *data* order (so autoplay/dots/i18n content
 *   order never has to be reversed) and calls `Element.scrollIntoView({ inline: 'start' })` on the
 *   target slide — `scrollIntoView`'s `inline: 'start'/'end'` are logical, direction-aware values,
 *   so the browser computes the correct physical scroll offset for either direction. Only the
 *   chevron *glyphs* are mirrored in CSS (`[dir='rtl'] svg { transform: scaleX(-1) }`) and the
 *   ArrowLeft/ArrowRight keyboard shortcuts are re-mapped (see `handleKeyDown`) — physical "visual
 *   right" should always move the viewport right, whichever language is active.
 */
export function Carousel({
  slides,
  dir,
  ariaLabel,
  prevLabel,
  nextLabel,
  slideLabel,
  goToLabel,
  autoplayMs,
  className,
}: CarouselProps) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const viewportRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<Array<HTMLLIElement | null>>([]);
  const ratiosRef = useRef<Map<number, number>>(new Map());
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = slides.length;

  const scrollToIndex = useCallback(
    (index: number) => {
      const viewport = viewportRef.current;
      const el = slideRefs.current[index];
      if (!viewport || !el) return;
      // Scroll the carousel's OWN viewport horizontally — never `scrollIntoView`, which also scrolls
      // ancestor scroll containers (incl. the document) and would yank the whole PAGE down to the
      // carousel, especially on autoplay while the section is off-screen. Adjusting `scrollLeft` by
      // the observed physical gap is correct in both LTR and RTL regardless of the browser's RTL
      // scrollLeft sign convention, and can never move the document's vertical scroll position.
      const vRect = viewport.getBoundingClientRect();
      const sRect = el.getBoundingClientRect();
      const delta = dir === "rtl" ? sRect.right - vRect.right : sRect.left - vRect.left;
      viewport.scrollTo({
        left: viewport.scrollLeft + delta,
        behavior: prefersReducedMotion ? "auto" : "smooth",
      });
      setActiveIndex(index);
    },
    [dir, prefersReducedMotion],
  );

  const goNext = useCallback(
    () => scrollToIndex((activeIndex + 1) % total),
    [activeIndex, total, scrollToIndex],
  );
  const goPrev = useCallback(
    () => scrollToIndex((activeIndex - 1 + total) % total),
    [activeIndex, total, scrollToIndex],
  );

  // Track which slide is actually most visible inside the scroll viewport (covers manual
  // touch/drag/trackpad scrolling too, not just button/dot clicks) so the dots + prev/next state
  // stay correct regardless of how the user moved the carousel.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const index = slideRefs.current.indexOf(entry.target as HTMLLIElement);
          if (index !== -1) ratiosRef.current.set(index, entry.intersectionRatio);
        }
        let bestIndex = 0;
        let bestRatio = -1;
        ratiosRef.current.forEach((ratio, index) => {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestIndex = index;
          }
        });
        setActiveIndex(bestIndex);
      },
      { root: viewport, threshold: [0.25, 0.5, 0.6, 0.75, 1] },
    );

    slideRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [total]);

  // Gentle autoplay — never enabled for reduced-motion users, and paused on hover/focus/tab
  // hidden so it never fights a reader mid-quote.
  useEffect(() => {
    if (!autoplayMs || prefersReducedMotion || paused) return;
    const id = window.setInterval(() => {
      setActiveIndex((current) => {
        const next = (current + 1) % total;
        const viewport = viewportRef.current;
        const el = slideRefs.current[next];
        if (viewport && el) {
          const vRect = viewport.getBoundingClientRect();
          const sRect = el.getBoundingClientRect();
          const delta = dir === "rtl" ? sRect.right - vRect.right : sRect.left - vRect.left;
          viewport.scrollTo({ left: viewport.scrollLeft + delta, behavior: "smooth" });
        }
        return next;
      });
    }, autoplayMs);
    return () => window.clearInterval(id);
  }, [autoplayMs, prefersReducedMotion, paused, total, dir]);

  useEffect(() => {
    function handleVisibility() {
      if (document.hidden) setPaused(true);
      else setPaused(false);
    }
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    // Arrow keys map to *physical* screen direction (Right always visually right), matching how
    // arrow-key navigation behaves everywhere else in the browser — so which logical
    // (prev/next) action that triggers has to flip for RTL.
    const rightMeansNext = dir === "ltr";
    if (event.key === "ArrowRight") {
      event.preventDefault();
      if (rightMeansNext) goNext();
      else goPrev();
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      if (rightMeansNext) goPrev();
      else goNext();
    } else if (event.key === "Home") {
      event.preventDefault();
      scrollToIndex(0);
    } else if (event.key === "End") {
      event.preventDefault();
      scrollToIndex(total - 1);
    }
  }

  function handleBlurCapture(event: FocusEvent<HTMLDivElement>) {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setPaused(false);
    }
  }

  return (
    <div
      className={`fa-carousel${className ? ` ${className}` : ""}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={handleBlurCapture}
    >
      <div
        className="fa-carousel__viewport"
        ref={viewportRef}
        role="region"
        aria-roledescription="carousel"
        aria-label={ariaLabel}
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <ul className="fa-carousel__track">
          {slides.map((slide, index) => (
            <li
              key={slide.id}
              ref={(el) => {
                slideRefs.current[index] = el;
              }}
              className="fa-carousel__slide"
              role="group"
              aria-roledescription="slide"
              aria-label={slideLabel(index + 1, total)}
            >
              {slide.content}
            </li>
          ))}
        </ul>
      </div>

      <div className="fa-carousel__controls">
        <button
          type="button"
          className="fa-carousel__arrow fa-carousel__arrow--prev"
          onClick={goPrev}
          aria-label={prevLabel}
        >
          <ArrowIcon />
        </button>
        <div className="fa-carousel__dots" role="group" aria-label={ariaLabel}>
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              className={`fa-carousel__dot${index === activeIndex ? " fa-carousel__dot--active" : ""}`}
              aria-current={index === activeIndex ? "true" : undefined}
              aria-label={goToLabel(index + 1, total)}
              onClick={() => scrollToIndex(index)}
            />
          ))}
        </div>
        <button
          type="button"
          className="fa-carousel__arrow fa-carousel__arrow--next"
          onClick={goNext}
          aria-label={nextLabel}
        >
          <ArrowIcon flipped />
        </button>
      </div>
    </div>
  );
}

/** A single chevron glyph, reused for both prev/next — `prev` points visually left by default and
 * `next` (`flipped`) points visually right; `[dir='rtl']` mirrors *both* again in CSS so each
 * button always points toward where its target slide actually is. */
function ArrowIcon({ flipped = false }: { flipped?: boolean }) {
  return (
    <svg
      viewBox="0 0 20 20"
      width="20"
      height="20"
      aria-hidden="true"
      focusable="false"
      className={`fa-carousel__arrow-icon${flipped ? " fa-carousel__arrow-icon--flipped" : ""}`}
    >
      <path
        d="M12.5 4.5L7 10l5.5 5.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
